import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollRestoration from './components/ScrollRestoration';
import { NavigationHistoryProvider } from './contexts/NavigationHistoryContext';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import ErrorBoundary from './components/ErrorBoundary';
import AIChatWidget from './components/AIChatWidget';
import { Toaster } from 'sonner';
// Install axios interceptors (attaches JWT to every API request)
import './lib/api';

// Lazy load heavy pages — only the visited page is downloaded
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Quotes = lazy(() => import('./pages/Quotes'));
const PandaDoc = lazy(() => import('./pages/PandaDoc'));
const OrdersCompact = lazy(() => import('./pages/OrdersCompact'));
const Production = lazy(() => import('./pages/Production'));
const Sales = lazy(() => import('./pages/Sales'));
const AnalyticsHub = lazy(() => import('./pages/AnalyticsHub'));
const OrderTracker = lazy(() => import('./pages/OrderTracker'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Documents = lazy(() => import('./pages/Documents'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const MyTasks = lazy(() => import('./pages/MyTasks'));

// Detect Supabase auth hash fragments (#access_token=...&type=invite)
// and redirect to /set-password so the invite flow works with BrowserRouter
function HashRedirect({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && (hash.includes('type=invite') || hash.includes('type=recovery'))) {
      // Preserve the hash so Supabase JS client can read the tokens
      window.history.replaceState(null, '', '/set-password' + hash);
    } else if (hash && hash.includes('error=')) {
      // Auth error (expired link, etc) — redirect to login with error info
      const params = new URLSearchParams(hash.substring(1));
      const errorDesc = params.get('error_description') || 'Authentication failed';
      window.history.replaceState(null, '', '/login?error=' + encodeURIComponent(errorDesc));
    }
    setReady(true);
  }, []);

  if (!ready) return null;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <Toaster richColors position="top-right" closeButton />
    <AuthProvider>
    <HashRedirect>
    <BrowserRouter>
      <NavigationHistoryProvider>
      <ScrollRestoration />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50"><div className="text-lg text-slate-500">Loading...</div></div>}>
      <main>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
          <Route path="/panda-doc" element={<ProtectedRoute><PandaDoc /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><OrdersCompact /></ProtectedRoute>} />
        <Route path="/pipeline" element={<Navigate to="/orders?tab=pipeline" replace />} />
        <Route path="/production" element={<ProtectedRoute><Production /></ProtectedRoute>} />
        <Route path="/sales" element={<ProtectedRoute requireSuperAdmin><Sales /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsHub /></ProtectedRoute>} />
        <Route path="/order-tracker" element={<ProtectedRoute><OrderTracker /></ProtectedRoute>} />
        <Route path="/stock" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
        <Route path="/inventory" element={<Navigate to="/stock" replace />} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/my-tasks" element={<ProtectedRoute><MyTasks /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute requireSuperAdmin><UserManagement /></ProtectedRoute>} />
        <Route path="/users" element={<Navigate to="/settings" replace />} />
      </Routes>
      </main>
      </Suspense>
      <AIChatWidget />
    </NavigationHistoryProvider>
    </BrowserRouter>
    </HashRedirect>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
